import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import {
  ExtensionAuthor,
  PromptAuthor,
  PromptBadges,
  usePromptFilterLocalizations,
  type Badge,
  type Prompt,
} from "@1js/pl-card-components";
import {
  isExtensionPrompt,
  isUserCreatedExtensionPrompt,
  type ExtensionPrompt,
  type UserExtensionPrompt,
} from "@1js/pl-types";
import {
  Link,
  Tag,
  Text,
  Tooltip,
  mergeClasses,
  useId,
} from "@fluentui/react-components";
import { Info16Regular } from "@fluentui/react-icons";
import * as React from "react";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { useFlightContext } from "../../hooks/useFlightContext";
import { useHandoffService } from "../../hooks/useHandoffService";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import type { WebPrompt } from "../data";
import {
  DetailDisplayCategoryDescription,
  DetailProductDescription,
  DetailProductDescriptionAlsoWorksIn,
  DetailProductDescriptionAlsoWorksInInfoLabel,
  DetailProductDescriptionUninstalledAgentHelpLink,
  DetailTagDescription,
  ExtensionPromptDescriptionInfoLabel,
} from "./PromptDescription.web.string";
import { useGoBoldStyles, useStyles } from "./PromptDescription.web.styles";
import { AuthorLabel } from "./PromptPreview/PromptPreview.web.string";

export type PromptDescriptionProps = {
  prompt: WebPrompt | Prompt;
  shouldShowAuthor: boolean;
};

export const PromptDescription: React.FC<PromptDescriptionProps> = ({
  prompt,
  shouldShowAuthor,
}) => {
  const { isFlightActive } = useFlightContext();
  const isGoBoldEnabled = isFlightActive("isGoBoldPromptCardEnabled");
  const legacyStyles = useStyles();
  const goBoldStyles = useGoBoldStyles();
  const styles = isGoBoldEnabled ? goBoldStyles : legacyStyles;
  const localizations = useStrings({
    AuthorLabel,
    DetailProductDescription,
    DetailProductDescriptionAlsoWorksIn,
    DetailProductDescriptionAlsoWorksInInfoLabel,
    DetailProductDescriptionUninstalledAgentHelpLink,
    DetailDisplayCategoryDescription,
    DetailTagDescription,
  });
  const localizationsWithPlaceholders = useStringsWithPlaceholders({
    ExtensionPromptDescriptionInfoLabel,
  });
  const { state } = React.useContext(LibraryContext);

  const { isTryInOnAuthed } = useFeatureEnablement();
  const { facetValueToLabel } = usePromptFilterLocalizations();

  const badges: Badge[] = prompt.Products.map((product: string) => {
    return {
      id: product,
    };
  });

  const { isHandoffSupported } = useHandoffService();

  const tryInBadgesCount = prompt.Products.filter((badge: string) =>
    isHandoffSupported(badge, prompt)
  ).length;
  const tryInByCopyBadges: Badge[] = prompt.Products.filter(
    (badge: string) => !isHandoffSupported(badge, prompt)
  ).map((product) => {
    return {
      id: product,
    };
  });

  const displayCategoryBadges: Badge[] = [
    {
      id: facetValueToLabel(prompt.DisplayCategory),
    },
  ];

  const alsoWorksInTooltipContentId = useId("AlsoWorksInTooltipContent");
  const [isAlsoWorksInTooltipVisible, setAlsoWorksInTooltipVisible] =
    React.useState(false);

  const isExtension: boolean = isExtensionPrompt(prompt);
  const isUserExtensionPrompt = isUserCreatedExtensionPrompt(prompt);
  const showToolTip =
    isExtension || (isTryInOnAuthed && tryInByCopyBadges.length > 0);

  const showHelpLink =
    isExtension &&
    isUserExtensionPrompt &&
    state.extensionIconMap &&
    !state.extensionIconMap[(prompt as UserExtensionPrompt).GptId as string];

  const helpLink = "https://go.microsoft.com/fwlink/p/?linkid=2301318";

  return (
    <div className={styles.container}>
      <div className={styles.column}>
        {isGoBoldEnabled && shouldShowAuthor && (
          <div className={styles.badge}>
            <Text className={styles.title}>{localizations.AuthorLabel()}</Text>
            {!isExtension || isUserExtensionPrompt ? (
              <PromptAuthor prompt={prompt} expanded={true} />
            ) : (
              <ExtensionAuthor prompt={prompt as ExtensionPrompt} />
            )}
          </div>
        )}
        {!isExtension && !isTryInOnAuthed && badges.length > 0 && (
          <div className={styles.badge} data-testid="productbadges">
            <Text className={styles.title}>
              {localizations.DetailProductDescription()}
            </Text>
            <PromptBadges
              badges={badges}
              maxNumber={5}
              displayMode="iconAndText"
              wrapOverflow={isGoBoldEnabled}
            />
          </div>
        )}
        {showToolTip && (
          <>
            <div className={styles.badge} data-testid="productbadges">
              <div
                aria-owns={
                  isAlsoWorksInTooltipVisible
                    ? alsoWorksInTooltipContentId
                    : undefined
                }
                className={styles.alsoWorksInTitleContainer}
              >
                <Text className={styles.title}>
                  {tryInBadgesCount === 0
                    ? localizations.DetailProductDescription()
                    : localizations.DetailProductDescriptionAlsoWorksIn()}
                </Text>
                <Tooltip
                  content={{
                    children: isExtension
                      ? localizationsWithPlaceholders.ExtensionPromptDescriptionInfoLabel(
                          {
                            extensionName: isUserExtensionPrompt
                              ? ((prompt as UserExtensionPrompt)
                                  .GptName as string)
                              : (prompt.ProviderName as string),
                          }
                        )
                      : localizations.DetailProductDescriptionAlsoWorksInInfoLabel(),
                    id: alsoWorksInTooltipContentId,
                  }}
                  positioning="above-start"
                  withArrow
                  relationship="label"
                  onVisibleChange={(_, data) =>
                    setAlsoWorksInTooltipVisible(data.visible)
                  }
                >
                  <Info16Regular
                    data-testid="detail-modal-tooltip"
                    tabIndex={0}
                    className={mergeClasses(
                      styles.tooltipIcon,
                      isAlsoWorksInTooltipVisible && styles.visibleTooltipIcon
                    )}
                  />
                </Tooltip>
              </div>
              <div className={styles.badgeContainer}>
                {isUserExtensionPrompt ? (
                  <PromptAuthor
                    prompt={prompt}
                    expanded={true}
                    extensionIconMap={state.extensionIconMap}
                    useAgentAsAuthor={true}
                  />
                ) : (
                  isExtension && (
                    <PromptAuthor
                      prompt={prompt}
                      expanded={true}
                      extensionIconMap={state.extensionIconMap}
                    />
                  )
                )}
                <PromptBadges
                  badges={tryInByCopyBadges}
                  maxNumber={5}
                  displayMode="iconAndText"
                  wrapOverflow={isGoBoldEnabled}
                />
              </div>
            </div>
            {showHelpLink && (
              <div
                className={styles.helpLink}
                data-testid="detail-modal-helpLink"
              >
                <Link href={helpLink} target="_blank">
                  <Text>
                    {localizations.DetailProductDescriptionUninstalledAgentHelpLink()}
                  </Text>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
      <div className={styles.column}>
        {prompt.Origin !== "User" && prompt.DisplayCategory && (
          <div className={styles.badge}>
            <Text className={styles.category}>
              {localizations.DetailDisplayCategoryDescription()}
            </Text>
            <PromptBadges
              badges={displayCategoryBadges}
              maxNumber={5}
              displayMode="textOnly"
              wrapOverflow={isGoBoldEnabled}
            />
          </div>
        )}
        {isGoBoldEnabled && prompt.PromptLabel && (
          <div className={styles.badge}>
            <Text className={styles.title}>
              {localizations.DetailTagDescription()}
            </Text>
            <Tag size="small">{prompt.PromptLabel}</Tag>
          </div>
        )}
      </div>
    </div>
  );
};
